import React from 'react'
import { Box, Paper, Stack, useTheme } from '@mui/material'
import { Bounce } from 'react-awesome-reveal'
import { Lightbulb, AutoAwesome, RocketLaunch } from '@mui/icons-material'

export const CreativeAnimation: React.FC = () => {
    const theme = useTheme()
    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '900px',
                height: { xs: '300px', md: '500px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
            }}
        >
            {/* Animated rotating gradient background - square box with rounded corners */}
            <Box
                sx={{
                    position: 'absolute',
                    width: { xs: '250px', md: '400px' },
                    height: { xs: '250px', md: '400px' },
                    borderRadius: 4,
                    background: `conic-gradient(
                        from 0deg,
                        ${theme.palette.primary.light}40,
                        ${theme.palette.secondary.light}40,
                        ${theme.palette.warning.light}40,
                        ${theme.palette.error.light}40,
                        ${theme.palette.primary.light}40
                    )`,
                    animation: 'gradientRotate 8s linear infinite',
                    '@keyframes gradientRotate': {
                        '0%': {
                            transform: 'translate(-50%, -50%) rotate(0deg)',
                        },
                        '100%': {
                            transform: 'translate(-50%, -50%) rotate(360deg)',
                        },
                    },
                    top: '50%',
                    left: '50%',
                }}
            />
            <Stack
                direction="row"
                spacing={3}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                }}
            >
                <Bounce delay={100} triggerOnce>
                    <Paper
                        elevation={6}
                        sx={{
                            p: 3,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            boxShadow: `0 8px 32px ${theme.palette.primary.main}50`,
                            animation: 'float 3s ease-in-out infinite',
                            animationDelay: '0s',
                            '@keyframes float': {
                                '0%, 100%': {
                                    transform: 'translateY(0px) rotate(0deg)',
                                },
                                '50%': {
                                    transform: 'translateY(-20px) rotate(5deg)',
                                },
                            },
                            '&:hover': {
                                animation: 'pulse 0.6s ease-in-out',
                                '@keyframes pulse': {
                                    '0%, 100%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.15)' },
                                },
                            },
                        }}
                    >
                        <Lightbulb
                            sx={{
                                fontSize: { xs: 40, md: 60 },
                                color: 'primary.contrastText',
                                animation: 'glow 2s ease-in-out infinite',
                                animationDelay: '0s',
                                '@keyframes glow': {
                                    '0%, 100%': {
                                        filter: 'drop-shadow(0 0 5px currentColor)',
                                    },
                                    '50%': {
                                        filter: 'drop-shadow(0 0 15px currentColor)',
                                    },
                                },
                            }}
                        />
                    </Paper>
                </Bounce>
                <Bounce delay={300} triggerOnce>
                    <Paper
                        elevation={6}
                        sx={{
                            p: 3,
                            borderRadius: '50%',
                            bgcolor: 'secondary.main',
                            boxShadow: `0 8px 32px ${theme.palette.secondary.main}50`,
                            animation: 'float 3s ease-in-out infinite',
                            animationDelay: '0.5s',
                            '@keyframes float': {
                                '0%, 100%': {
                                    transform: 'translateY(0px) rotate(0deg)',
                                },
                                '50%': {
                                    transform:
                                        'translateY(-20px) rotate(-5deg)',
                                },
                            },
                            '&:hover': {
                                animation: 'pulse 0.6s ease-in-out',
                                '@keyframes pulse': {
                                    '0%, 100%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.15)' },
                                },
                            },
                        }}
                    >
                        <AutoAwesome
                            sx={{
                                fontSize: { xs: 40, md: 60 },
                                color: 'secondary.contrastText',
                                animation: 'sparkle 2s ease-in-out infinite',
                                animationDelay: '0.5s',
                                '@keyframes sparkle': {
                                    '0%, 100%': {
                                        transform: 'rotate(0deg) scale(1)',
                                        filter: 'drop-shadow(0 0 5px currentColor)',
                                    },
                                    '25%': {
                                        transform: 'rotate(90deg) scale(1.1)',
                                        filter: 'drop-shadow(0 0 10px currentColor)',
                                    },
                                    '50%': {
                                        transform: 'rotate(180deg) scale(1)',
                                        filter: 'drop-shadow(0 0 15px currentColor)',
                                    },
                                    '75%': {
                                        transform: 'rotate(270deg) scale(1.1)',
                                        filter: 'drop-shadow(0 0 10px currentColor)',
                                    },
                                },
                            }}
                        />
                    </Paper>
                </Bounce>
                <Bounce delay={500} triggerOnce>
                    <Paper
                        elevation={6}
                        sx={{
                            p: 3,
                            borderRadius: '50%',
                            bgcolor: 'warning.main',
                            boxShadow: `0 8px 32px ${theme.palette.warning.main}50`,
                            animation: 'float 3s ease-in-out infinite',
                            animationDelay: '1s',
                            '@keyframes float': {
                                '0%, 100%': {
                                    transform: 'translateY(0px) rotate(0deg)',
                                },
                                '50%': {
                                    transform: 'translateY(-20px) rotate(5deg)',
                                },
                            },
                            '&:hover': {
                                animation: 'pulse 0.6s ease-in-out',
                                '@keyframes pulse': {
                                    '0%, 100%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.15)' },
                                },
                            },
                        }}
                    >
                        <RocketLaunch
                            sx={{
                                fontSize: { xs: 40, md: 60 },
                                color: 'warning.contrastText',
                                animation: 'rocket 2.5s ease-in-out infinite',
                                animationDelay: '1s',
                                '@keyframes rocket': {
                                    '0%, 100%': {
                                        transform:
                                            'translateY(0px) rotate(-45deg)',
                                        filter: 'drop-shadow(0 0 5px currentColor)',
                                    },
                                    '50%': {
                                        transform:
                                            'translateY(-10px) rotate(-35deg)',
                                        filter: 'drop-shadow(0 0 15px currentColor)',
                                    },
                                },
                            }}
                        />
                    </Paper>
                </Bounce>
            </Stack>
        </Box>
    )
}
